import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CustomToast from '../../components/Redesing/custom-toast';
import { ChatContextRental, createSupportTicket } from '../../hooks/chat/api';
import { systemLabel } from '../../hooks/chat/mappers';
import { Conversation, Message } from '../../hooks/useChat';
import { PanelSection } from './PanelSection';
import { ActionButton } from './ActionButton';
import {
  TICKET_CONTENT_MAX,
  buildTicketContent,
  supportTicketInput,
  ticketNoteText,
  ticketUserOldId,
} from './supportTicket';

interface Props {
  conversation: Conversation;
  messages: Message[];
  /** The anchored rental, for its admin_api id and system. */
  rental: ChatContextRental | null;
  agentName: string;
  /**
   * Posts the "Support ticket #… created" internal note. Not called on a
   * closed session: ant refuses notes there, and the panel says it instead.
   */
  onAddNote?: (convId: string, note: string) => Promise<void>;
}

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : String(error ?? '');

/**
 * Files a support ticket (ant's createSupportTicket) from the chat: the
 * rider by phone, the system and rental when the chat has them, and a
 * summary plus the last public messages, which the agent can edit first.
 * The chat keeps an internal note of it, and the panel links the ticket.
 */
export function SupportTicketSection({
  conversation,
  messages,
  rental,
  agentName,
  onAddNote,
}: Props) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<{ id: string; number: string } | null>(null);

  useEffect(() => {
    setCreated(null);
    setOpen(false);
  }, [conversation.id]);

  const closed = conversation.status === 'closed';
  const length = content.trim().length;
  const tooLong = length > TICKET_CONTENT_MAX;
  const rentalOldId = rental?.oldId ?? null;
  const systemOldId = rental?.system?.oldId ?? conversation.system_old_id ?? null;
  const blocker = ticketUserOldId(conversation.user_phone)
    ? null
    : "The rider's phone is not known, and a ticket needs it.";

  const start = () => {
    setContent(buildTicketContent({ conversation, messages, agentName, rentalOldId }));
    setOpen(true);
  };

  const create = async () => {
    const input = supportTicketInput({
      conversation,
      content: content.trim(),
      systemOldId,
      rentalOldId,
    });
    if (!input) return;
    setBusy(true);
    try {
      const ticket = await createSupportTicket(input);
      const number = ticket.oldId || ticket.id;
      setCreated({ id: ticket.id, number });
      setOpen(false);
      CustomToast({ type: 'success', message: `Support ticket #${number} created` });
      if (onAddNote && !closed) {
        try {
          await onAddNote(conversation.id, ticketNoteText(number));
        } catch (error) {
          CustomToast({
            type: 'error',
            message: `The ticket was created, but the chat note was not. ${errorText(
              error
            )}`,
            duration: 6000,
          });
        }
      }
    } catch (error) {
      CustomToast({
        type: 'error',
        message: `Could not create the support ticket. ${errorText(error)}`,
        duration: 6000,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <PanelSection title="Support ticket" testId="support-ticket-section">
      {created ? (
        <Stack spacing={0.5}>
          <Typography variant="body2" data-testid="ticket-created">
            Ticket #{created.number} created.
          </Typography>
          {closed && (
            <Typography variant="caption" color="text.secondary">
              The chat is closed, so no note was added to it.
            </Typography>
          )}
          <Link
            href={`/#/problems/${created.number}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            Open ticket <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        </Stack>
      ) : (
        <ActionButton
          label="Create support ticket"
          icon={<ConfirmationNumberOutlinedIcon />}
          blocker={blocker}
          hint="File a ticket with this chat for follow-up"
          color="primary"
          onClick={start}
        />
      )}

      <Dialog
        open={open}
        onClose={busy ? undefined : () => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create support ticket</DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2" sx={{ mb: 2 }}>
            For {conversation.user_phone}
            {conversation.system_name ? ` in ${systemLabel(conversation)}` : ''}
            {rentalOldId ? `, rental ${rentalOldId}` : ''}. Edit the text if you need to.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={8}
            maxRows={18}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            inputProps={{ 'aria-label': 'Ticket content' }}
            error={tooLong}
            helperText={`${length.toLocaleString(
              'en-US'
            )} / ${TICKET_CONTENT_MAX.toLocaleString('en-US')} characters${
              tooLong ? ': shorten it to create the ticket' : ''
            }`}
            sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={create}
            disabled={busy || !content.trim() || tooLong}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Create ticket
          </Button>
        </DialogActions>
      </Dialog>
    </PanelSection>
  );
}
